"use client";

// How eagerly the core image components (ImageComponent, MediaComponent, ImageServerComponent) load an image. A site
// sets it per region of the page instead of on every image:
//
//   <ImagePriorityProvider value="lazy">                     root layout: lazy unless a region says otherwise
//     <ImagePriorityProvider value="eager">header</...>      logo / banner: load now, no preload
//     <WidgetPriority index={i}>widget</WidgetPriority>     widget lists: the first 3 "preload", the rest "lazy"
//     <ImagePriorityProvider value={slide_priority(i, { hero: true })}>slide</...>   hero: first slide "lcp"
//
// Levels and the resolution order are in @core/helpers/image-priority (re-exported here). A site without any provider
// renders exactly as before.

import { createContext, useContext } from "react";
import { normalize_image_priority, widget_priority } from "@core/helpers/image-priority";

export {
	IMAGE_PRIORITY_LEVELS,
	normalize_image_priority,
	widget_priority,
	slide_priority,
	resolve_image_priority,
	level_from_next_props,
	visible_slide_count,
	image_priority_props,
	shows_skeleton,
} from "@core/helpers/image-priority";

const ImagePriorityContext = createContext(undefined);

export const ImagePriorityProvider = ({ value, children }) => (
	<ImagePriorityContext.Provider value={normalize_image_priority(value)}>{children}</ImagePriorityContext.Provider>
);

export const useImagePriority = () => useContext(ImagePriorityContext);

/** Wraps one widget of a page's widget list: the first `first` widgets "preload", the rest "lazy". */
export const WidgetPriority = ({ index, first = 3, children }) => (
	<ImagePriorityProvider value={widget_priority(index, first)}>{children}</ImagePriorityProvider>
);

/**
 * A slider's slides with every slide past the first `visible` wrapped in a "lazy" provider: a slider inside a
 * "preload" widget preloads only what is on screen, not all its slides. Slides that set their own level keep it.
 */
export const limit_slide_priority = (slides, visible) =>
	Array.isArray(slides)
		? slides.map((slide, index) =>
				index < visible ? (
					slide
				) : (
					<ImagePriorityProvider key={slide?.key ?? index} value="lazy">
						{slide}
					</ImagePriorityProvider>
				),
			)
		: slides;

const ImageDeviceContext = createContext(undefined);
const ImageHiddenContext = createContext(false);

/**
 * The visitor's device class for images: "mobile" or "desktop" (see image_device_server), set once in the root
 * layout. Only read by ImagesOnlyOn.
 */
export const ImageDeviceProvider = ({ value, children }) => (
	<ImageDeviceContext.Provider value={value || undefined}>{children}</ImageDeviceContext.Provider>
);

/**
 * Wraps a block the CSS shows on one device class only (a `lg:hidden` mobile copy, a `hidden lg:block` desktop copy).
 * On the other device its images are "lazy", even ones marked "lcp" (the visible copy keeps that): a hidden lazy image is never downloaded, while a preloaded one is, so
 * without this a widget with both copies downloads every image twice. Unknown device (tablet, no provider) → no
 * change.
 */
export const ImagesOnlyOn = ({ device, children }) => {
	const current = useContext(ImageDeviceContext);
	if (!current || !device || current === device) return children;
	return <ImageHiddenContext.Provider value={true}>{children}</ImageHiddenContext.Provider>;
};

/** true inside an ImagesOnlyOn block for the other device: its images stay lazy even with an explicit level. */
export const useImageHidden = () => useContext(ImageHiddenContext);
